import React, { useState, useMemo, useContext } from "react";
import { PiShoppingCartThin } from "react-icons/pi";
import { Link } from "react-router-dom";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import axios from "axios";
import AccountLayout from "./AccountLayout";
import useMyOrders from "../libs/useMyOrders";
import { AuthContext } from "../../../Context/AuthContext";
import { AccountOrdersSkeleton } from "./AccountSkeletons";
import OrderPaymentBadge, { canCancelOrder } from "../components/OrderPaymentBadge";
import RetryPaymentButton from "../components/RetryPaymentButton";
import { isPayOnDeliveryOrder, formatNgn } from "../libs/orderDisplayUtils";
import "./UserProfile.css";

const formatPrice = (amount) =>
  amount?.toLocaleString("en-US", { style: "currency", currency: "NGN" }) || "";

const Customerorder = () => {
  const [ongoingDeliver, setOngoingDeliver] = useState(true);
  const { data: orders = [], isLoading, isError } = useMyOrders();
  const { token } = useContext(AuthContext);
  const queryClient = useQueryClient();

  const ongoingOrders = useMemo(
    () => orders.filter((o) => ["Pending", "Processing", "Shipped"].includes(o.status)),
    [orders]
  );
  const deliveredOrders = useMemo(
    () => orders.filter((o) => o.status === "Delivered"),
    [orders]
  );
  const closedOrders = useMemo(
    () => orders.filter((o) => o.status === "Canceled"),
    [orders]
  );

  const displayOrders = ongoingDeliver
    ? [...ongoingOrders, ...deliveredOrders]
    : closedOrders;

  const handleCancel = async (orderId) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_SERVER_URL}/order/orders/${orderId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Order canceled");
      queryClient.invalidateQueries("myOrders");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not cancel order");
    }
  };

    if (isLoading) {
        return (
            <AccountLayout loading>
                <AccountOrdersSkeleton />
            </AccountLayout>
        );
    }

    return (
        <AccountLayout title="Orders">
            <div className="account-tabs">
        <button
          type="button"
          className={ongoingDeliver ? "active" : ""}
          onClick={() => setOngoingDeliver(true)}
        >
          Ongoing / Delivered
        </button>
        <button
          type="button"
          className={!ongoingDeliver ? "active" : ""}
          onClick={() => setOngoingDeliver(false)}
        >
          Canceled
        </button>
      </div>

            {isError && <p className="account-error">Failed to load orders.</p>}

            {displayOrders.length === 0 && (
        <div className="account-empty">
          <div className="account-empty__icon">
            <PiShoppingCartThin />
          </div>
          <h3>{ongoingDeliver ? "You have placed no orders yet" : "No canceled orders"}</h3>
          <p>
            {ongoingDeliver
              ? "All your orders will be saved here so you can track them anytime."
              : "Canceled orders will appear here."}
          </p>
          {ongoingDeliver && (
            <Link to="/market" className="account-btn account-btn--secondary">
              Start shopping
            </Link>
          )}
        </div>
      )}

      {displayOrders.length > 0 && (
        <div className="order-list">
          {displayOrders.map((order) => (
            <div key={order._id} className="order-card">
              <div className="order-card-header">
                <div>
                  <Link to={`/account/orders/${order._id}`}>
                    <strong>{order.orderNumber}</strong>
                  </Link>
                  <p className="order-date">{new Date(order.createdAt).toLocaleDateString()}</p>
                </div>
                <div className="order-card-badges">
                  <span className="order-status-badge">{order.status}</span>
                  <OrderPaymentBadge order={order} />
                </div>
              </div>
              {isPayOnDeliveryOrder(order) && order.paymentStatus === "pending" && (
                <p className="order-payment-badge-due">
                  Pay {formatNgn(order.totalAmount)} when delivered
                </p>
              )}
              <ul className="order-items-list">
                {(order.items || []).map((item) => (
                  <li key={item._id}>
                    {item.name} x{item.quantity}
                  </li>
                ))}
              </ul>
              <p className="order-total">
                <strong>{formatPrice(order.totalAmount)}</strong>
              </p>
              <div className="order-card-actions">
                <Link to={`/market/track-order?orderNumber=${order.orderNumber}`}>Track</Link>
                <Link to={`/account/orders/${order._id}`}>View details</Link>
                <RetryPaymentButton order={order} />
                {canCancelOrder(order) && (
                  <button type="button" className="order-action--danger" onClick={() => handleCancel(order._id)}>
                    Cancel
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </AccountLayout>
  );
};

export default Customerorder;
